/**
 * Thin wrapper around the Telegram WebApp runtime that ships with telegram-web-app.js.
 *
 * Why hand-roll instead of using @telegram-apps/sdk-react?
 *   - We only need a tiny surface (initData, themeParams, MainButton, BackButton, haptics).
 *   - Outside Telegram (regular browser, dev) every method must no-op gracefully.
 *
 * NOTE: Real wiring to nu--comments-chats backend will use a JWT/HMAC built from
 *       Telegram.WebApp.initData (validated server-side). For now we just expose
 *       initDataUnsafe.user so the UI can show the right name/avatar.
 */

declare global {
  interface Window {
    Telegram?: {
      WebApp?: TelegramWebApp;
    };
  }
}

export interface TgUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  language_code?: string;
}

interface TelegramWebApp {
  ready: () => void;
  expand: () => void;
  close: () => void;
  initData: string;
  initDataUnsafe: { user?: TgUser };
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string>;
  MainButton: {
    setText: (t: string) => void;
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
    enable: () => void;
    disable: () => void;
  };
  BackButton: {
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  HapticFeedback: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    selectionChanged: () => void;
    notificationOccurred: (type: 'success' | 'warning' | 'error') => void;
  };
}

export function getWebApp(): TelegramWebApp | null {
  return typeof window !== 'undefined' && window.Telegram?.WebApp
    ? window.Telegram.WebApp
    : null;
}

export function initTelegram(): void {
  const tg = getWebApp();
  if (!tg) return;
  try {
    tg.ready();
    tg.expand();
    // Force dark — naш design всегда тёмный.
    document.documentElement.classList.add('dark');
  } catch {
    /* ignore — older clients */
  }
}

export function getTgUser(): TgUser | null {
  return getWebApp()?.initDataUnsafe.user ?? null;
}

export function haptic(type: 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error' = 'light'): void {
  const tg = getWebApp();
  if (!tg) return;
  try {
    if (type === 'success' || type === 'warning' || type === 'error') {
      tg.HapticFeedback.notificationOccurred(type);
    } else {
      tg.HapticFeedback.impactOccurred(type);
    }
  } catch {
    /* noop */
  }
}

export function showBackButton(handler: () => void): () => void {
  const tg = getWebApp();
  if (!tg) return () => {};
  tg.BackButton.show();
  tg.BackButton.onClick(handler);
  return () => {
    tg.BackButton.hide();
    tg.BackButton.offClick(handler);
  };
}
